import React, { Component } from "react";
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux';
import ReactGA from 'react-ga';
import classNames from 'classnames';
import moment from 'moment';
import Cookies from 'universal-cookie';

// Material UI
import { withStyles, fade } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Menu from '@material-ui/core/Menu';
import InputBase from '@material-ui/core/InputBase';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';

// Material UI Icons
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/CloseRounded';
import ColumnIcon from '@material-ui/icons/ViewColumnRounded';
import ReplayIcon from '@material-ui/icons/ReplayRounded';
import SearchIcon from '@material-ui/icons/SearchRounded';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import IconChevronRight from '@material-ui/icons/ChevronRightRounded';
import ArrowForwardIcon from '@material-ui/icons/ArrowForwardRounded';

// Components
import * as actions from '../../store/actions/index';
import Filter from './InventoryList/Components/Filter';
import Download from './InventoryList/Components/Download';
import Print from './InventoryList/Components/Print';
import { sortSpecies, sortNumAlpha, stableSort, getSorting } from './Utilities/utils';
import { getFullSpecieParameterList, getSpeciesListBasedOnCategory } from './Utilities/speciesUtil';
import { getActionUnitsAndPoints } from './Utilities/biotopeUtils';

// Get rows
import * as rowsList from './Utilities/listRows';

// Links
// Søg eks. https://codesandbox.io/s/eloquent-clarke-w1ehv?file=/src/index.js:2624-2634
// Material UI V3. https://v3.material-ui.com/demos/tables/#tables

// Auxillary libs
const _ = require("underscore");
const species = require('./Utilities/updatedSpeciesList.json');
const cookies = new Cookies();

class EnhancedTableHead extends Component {
    createSortHandler = property => event => {
        this.props.onRequestSort(event, property);
    };

    render() {
        const { order, orderBy, rows } = this.props;

        return (
            <TableHead>
                <TableRow>
                    {rows.map(row => (
                        <React.Fragment key={row.id}>
                            {row.checked && <TableCell
                                style={{
                                    backgroundColor: 'white',
                                    minWidth: row.id === 'year' ? 120 :
                                        row.id === 'area' || row.id === 'action' ? 105 : 40
                                }}
                                key={row.id}
                                align='left'
                                padding='default'
                                sortDirection={orderBy === row.id ? order : false}
                            >
                                <Tooltip
                                    title="Sorter"
                                    placement={row.numeric ? 'bottom-end' : 'bottom-start'}
                                    enterDelay={300}
                                >
                                    <TableSortLabel
                                        active={orderBy === row.id}
                                        direction={order}
                                        onClick={this.createSortHandler(row.id)}
                                        IconComponent={ArrowDropDownIcon}
                                    >
                                        {row.label}
                                    </TableSortLabel>
                                </Tooltip>
                            </TableCell>}
                        </React.Fragment>
                    ))}
                </TableRow>
            </TableHead>
        );
    }
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
};

const styles = theme => ({
    paper: { // ok
        width: '100%',
    },
    root: { // ok
        zIndex: 102,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        width: '100%',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        padding: 20,
    },
    table: {
        width: '100%',
        borderBottom: '1px solid #E0E0E0'
    },
    tableWrapper: {
        overflowX: 'auto',
        overflowY: 'auto',
    },
    // Toolbar
    toolbarRoot: {
        paddingRight: theme.spacing.unit,
    },
    spacer: {
        flex: '1 1 100%',
    },
    actions: {
        color: theme.palette.text.secondary,
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-end',
        alignItems: 'center'
    },
    title: {
        flex: '0 0 auto',
    },
    // Menu Containers
    menuContainer: {
        padding: 16,
    },
    naviContainer: {
        paddingLeft: 8,
        paddingRight: 8
    },
    // Search
    search: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: fade(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: fade(theme.palette.common.white, 0.25),
        },
        marginLeft: 0,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginLeft: 0,
            width: 'auto',
        },
    },
    searchIcon: {
        padding: theme.spacing(0, 2),
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    inputRoot: {
        color: 'inherit',
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: '12ch',
            '&:focus': {
                width: '20ch',
            },
        },
    },
    cellNoBorder: {
        borderBottom: "none",
        whiteSpace: 'pre-line'
    },
    cellStyle: {
        borderTop: '1px solid #E0E0E0',
        whiteSpace: 'pre-line'
    },
    cellStyleItalic: {
        borderTop: '1px solid #E0E0E0',
        fontStyle: 'italic',
    },
    bodyFontItalic: {
        fontStyle: 'italic',
    },
    cellStyleMaxReached: {
        borderTop: '1px solid #E0E0E0',
        fontWeight: 'bold',
    }
});

class EnhancedTable extends Component {
    state = {
        // Table setup
        order: 'asc',
        orderBy: 'action',
        numeric: true,
        page: 0,
        rowsPerPage: 10,
        anchorEl: null,
        anchorEb: null,
        anchorNa: {
            xPos: null,
            yPos: null,
        },
        naviData: null,
        searchText: '',
        height: window.innerHeight,

        // Fetch columns from imported file
        rows: [
            { id: "title", checked: true, numeric: false, disablePadding: true, label: "Navn eller nr." },
            { id: "actionNumber", checked: true, numeric: true, disablePadding: true, label: "Tiltags nr.", initRange: [], newRange: [] },
            { id: "action", checked: true, numeric: false, disablePadding: true, label: "Tiltag" },
            { id: "year", checked: true, numeric: true, disablePadding: true, label: "Etableringsår / (alder)", initRange: [], newRange: [] },
            { id: "lineLength", checked: true, numeric: true, disablePadding: true, label: "Længde [m]", initRange: [], newRange: [] },
            { id: "area", checked: true, numeric: true, disablePadding: true, label: "Areal", initRange: [], newRange: [] },
            { id: "units", checked: true, numeric: true, disablePadding: true, label: "Antal enheder", initRange: [], newRange: [] },
            { id: "points", checked: true, numeric: true, disablePadding: true, label: "Point", initRange: [], newRange: [] },
            { id: "note", checked: true, numeric: false, disablePadding: true, label: "Bemærk." },
        ],

        // Actions value object. Used for calculating units and points
        actionsValues: {},

        // Load list data from props
        listData: { ...this.props.biotopePlanFeatures[this.props.selectedBiotopePlan], ...this.props.biotopePlanPoints[this.props.selectedBiotopePlan] },
        listDataNew: null,
        listDataShow: null,
        listSummaryData: [],
        tableBodyContent: [],
        updateContent: true,

        // Summary data
        planType: this.props.biotopePlansUser[this.props.selectedBiotopePlan].planType ?
            (this.props.biotopePlansUser[this.props.selectedBiotopePlan].planType === '1 årig' ? 1 :
                this.props.biotopePlansUser[this.props.selectedBiotopePlan].planType === '3 årig' ? 3 : 5)
            : null,
        naturePlan: this.props.biotopePlansUser[this.props.selectedBiotopePlan].naturePlan,
        totalArea: 0,
        cultivatedArea: this.props.biotopePlansUser[this.props.selectedBiotopePlan].cultivatedArea ? this.props.biotopePlansUser[this.props.selectedBiotopePlan].cultivatedArea : null,
        featureNum: -1,


        // Filter states
        bpFilterActions: [],
        bpFilterRanges: [],
        bpFilterActionsActive: false,
        bpFilterRangeActive: false,
        bpFilterSearchActive: false,
    };

    componentWillMount() {
        // unpack data for list
        let actionList = [];
        let list = [];
        Object.values(this.state.listData).forEach((feat) => {
            // differentiate between points and polygons/lines
            if (feat.planId) {
                // Feature is point
                // Format data
                const itemContent = this.listItemContent(feat, this.state.cultivatedArea);
                // Add to actions type list
                if (!actionList.includes(itemContent.action)) {
                    actionList.push(itemContent.action);
                }
                // Set filter range values
                this.setRangeValues(itemContent);
                // Push item to list
                list.push(itemContent);
            } else {
                // Feature id polygon or line
                // Check for plan boarder polygon (used to calculate total area)
                if (feat.cellData.species === "Afgrænsning") {
                    this.setState({ totalArea: feat.cellData.area })
                } else {
                    // Check for combination lines
                    if (Object.keys(feat.cellData.speciesObjs).length === 3 && (feat.cellData.species === "8: Kombination af 3 tiltag, tiltag 1 samt 3-7 samlet" || feat.cellData.species === "9: Kombination af 3 tiltag, tiltag 2 samt 3-7 samlet")) {
                        // If there are more than one object it must be actyion type 8 or 9
                        let data = {
                            main: true,
                            litra: feat.cellData.litra,
                            note: feat.cellData.note,
                            area: feat.cellData.area,
                            lineLength: feat.cellData.lineLength,
                            polyId: feat.cellData.id,
                            geoType: feat.cellData.type,
                            year: feat.cellData.year,
                            species: feat.cellData.species,
                        }
                        // Convert data
                        const itemContent = this.listItemContent(data, this.state.cultivatedArea);
                        // Add to actions type list
                        if (!actionList.includes(itemContent.action)) {
                            actionList.push(itemContent.action);
                        }
                        // Set filter range values
                        this.setRangeValues(itemContent);
                        // Push item to list
                        list.push(itemContent);
                    }
                    // Go through object list
                    Object.values(feat.cellData.speciesObjs).forEach(el => {
                        // Form data object
                        let addData = { ...el };
                        if (addData.note) {
                            addData.note = feat.cellData.note;
                        }
                        let data = {
                            litra: feat.cellData.litra,
                            note: feat.cellData.note,
                            area: feat.cellData.area,
                            lineLength: feat.cellData.lineLength,
                            polyId: feat.cellData.id,
                            geoType: feat.cellData.type,
                            ...addData
                        }
                        // Convert data
                        const itemContent = this.listItemContent(data, this.state.cultivatedArea);
                        // Add to actions type list
                        if (!actionList.includes(itemContent.action)) {
                            actionList.push(itemContent.action);
                        }
                        // Set filter range values
                        this.setRangeValues(itemContent);
                        // Push item to list
                        list.push(itemContent);
                    })
                }
            }
        });
        // Sort the list
        list = list.sort((a, b) => a.actionNumber - b.actionNumber);
        this.setState({ listDataNew: list })

        // Set list data to show
        this.setState({ listDataShow: list })

        // Init filters
        if (!this.props.filterState) {
            // Actions
            let aList = actionList.map(spc => {
                return { label: spc, checked: true }
            });
            aList = sortSpecies(aList)
            // Ranges
            const rList = this.state.rows.filter(row => {
                return row.initRange !== undefined && row.initRange.length > 0 && row.initRange[0] !== row.initRange[1];
            });
            // Set state
            this.setState({ bpFilterActions: aList, bpFilterRanges: rList })
        } else {
            // Set state from reducer state
            this.setState({
                bpFilterActions: this.props.filterState.bpFilterActions,
                bpFilterRanges: this.props.filterState.bpFilterRanges,
                searchText: this.props.filterState.searchText,
                bpFilterActionsActive: this.props.filterState.bpFilterActionsActive,
                bpFilterRangeActive: this.props.filterState.bpFilterRangeActive,
                bpFilterSearchActive: this.props.filterState.bpFilterSearchActive,
            })
        }
        window.addEventListener('resize', this.handleWindowSizeChange);
    }

    setRangeValues = (itemContent) => {
        // Find range of values
        Object.values(this.state.rows).forEach(el => {
            if ('initRange' in el) {
                if (itemContent[el.id] !== '-' && itemContent[el.id] !== '- (-)') {
                    let tVar = parseFloat(itemContent[el.id].split(',').join('.'));
                    if (el.id === 'year') {
                        tVar = parseFloat(itemContent[el.id].split('(').pop().split(')')[0])
                    }
                    if (el.initRange.length < 1) {
                        el.initRange[0] = tVar;
                        el.initRange[1] = tVar;
                    } else {
                        if (tVar < el.initRange[0]) {
                            el.initRange[0] = tVar;
                        }
                        if (tVar > el.initRange[1]) {
                            el.initRange[1] = tVar;
                        }
                    }
                    el.newRange = el.initRange;
                }
            }
        })
    }

    componentWillUnmount() {
        // Set reducer state when unmounting
        this.props.onSetFilterState({
            bpFilterActions: [...this.state.bpFilterActions],
            bpFilterRanges: JSON.parse(JSON.stringify(this.state.bpFilterRanges)),
            searchText: this.state.searchText,
            bpFilterActionsActive: this.state.bpFilterActionsActive,
            bpFilterRangeActive: this.state.bpFilterRangeActive,
            bpFilterSearchActive: this.state.bpFilterSearchActive,
        })

        // Set cell ids in reducer state
        const cellIds = this.state.listSummaryData.map(el => {
            return el.id;
        })
        this.props.onSetFilterCellIds(cellIds)

        // Remove eventlistners 
        window.removeEventListener('resize', this.handleWindowSizeChange);
    }

    componentDidMount() {
        // Update columns based on stored cookies
        let columnState = [];
        columnState = cookies.get('columnStateBiotopePlan');
        if (columnState) {
            let newColumnState = [...this.state.rows];
            columnState.forEach(el => {
                if (!el.checked) {
                    const idx = newColumnState.findIndex(row => row.id === el.id);
                    newColumnState[idx].checked = false;
                }
            })
            this.setState({ rows: columnState })
        }
    }

    componentDidUpdate() {
        if (this.state.updateContent) {
            this.renderTableRows()
            this.setState({ updateContent: false })
        }
    }

    handleWindowSizeChange = () => {
        this.setState({ height: window.innerHeight });
    };

    applyFilters = () => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Filter ranges apply`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        this.setState({ updateContent: true })
    }

    resetFilters = () => {
        let updatedFilter = [...this.state.bpFilterRanges];
        for (const item of updatedFilter) {
            item.newRange = item.initRange;
        }
        this.setState({ updatedFilter, bpFilterRangeActive: false, updateContent: true })
    }

    onRangeChange(i, event, newValue) {
        let arr = [...this.state.bpFilterRanges];
        let currentFilterState = false;
        arr[i].newRange = newValue;
        for (const item of this.state.bpFilterRanges) {
            if (item.initRange[0] !== item.newRange[0] || item.initRange[1] !== item.newRange[1]) {
                currentFilterState = true;
            }
        }
        // this.setState({ arr, jiFilterRangeActive: currentFilterState })
        this.setState({ bpFilterRanges: arr, bpFilterRangeActive: currentFilterState })
    }

    onSpecieChange(i) {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Filter change action: ${this.state.bpFilterActions[i].label}, to state: ${!this.state.bpFilterActions[i].checked}`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        let arr = [...this.state.bpFilterActions];
        let currentFilterState = false;
        arr[i].checked = !arr[i].checked;
        for (const item of this.state.bpFilterActions) {
            if (!item.checked) {
                currentFilterState = true;
            }
        }

        this.setState({ arr, updateContent: true, bpFilterActionsActive: currentFilterState })
    }

    onSpecieChangeAll() {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Filter actions change all`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        let arr = [...this.state.bpFilterActions];
        let currentFilterState = false;
        for (const item of arr) {
            if (this.state.bpFilterActionsActive) {
                item.checked = true
            } else {
                item.checked = false
                currentFilterState = true
            }
        }

        this.setState({ arr, updateContent: true, bpFilterActionsActive: currentFilterState })
    }

    // Check all data entries and setup data for the list
    listItemContent = (itemData, cultivatedArea, combi) => {
        let id = '-';
        let title = '-';
        let actionNumber = '-';
        let action = '-';
        let year = '-';
        let lineLength = '-';
        let area = '-';
        let polyArea = '-';
        let units = '-';
        let points = '-';
        let note = '-';
        let geoType = null;
        let maxReached = false;
        let main = true;

        // Differentiate between points and polygons/lines
        if (itemData.planId) {
            // Points
            // Get item parameters
            const par = getFullSpecieParameterList(itemData.pointType);
            // Set item id
            id = itemData.id;
            // Set item title
            title = itemData.title;
            // Set action number
            actionNumber = par.actionNumber ? par.actionNumber.toString() : '-';
            // Set action type
            action = itemData.pointType.split(':')[1].trim();
            // Set year and age
            year = itemData.year ? parseInt(itemData.year) : "-";
            year = year > moment().year() ? '-' : year;
            const age = year !== '-' ? (moment().year() - year).toString() : '-';
            year = year.toString() + ' (' + age + ')';
            // Set lineLength
            if (itemData.lineLength && typeof itemData.lineLength === 'number') {
                lineLength = itemData.lineLength !== 0 ? itemData.lineLength.toFixed(0).split('.').join(',') : "-";
            }
            // Set area
            if (itemData.area && typeof itemData.area === 'number') {
                area = itemData.area.toFixed(2).replace('.', ',');
                polyArea = area;
            }
            // Set units and points
            if (action !== '-') {
                const out = getActionUnitsAndPoints(itemData.pointType, 'Point', 1, cultivatedArea, this.state.planType);
                maxReached = out.maxReached; 
                units = out.units.toFixed(2).replace('.', ',');
                points = out.points.toFixed(1).replace('.', ',');
            }
            // Set note
            if (typeof itemData.text === "number") {
                note = itemData.text.toString();
            } else {
                note = itemData.text === "" ? "-" : itemData.text;
            }
            // Set geometry type
            geoType = 'Point';
        } else {
            // Polygons and lines
            // Get item parameters
            const par = getFullSpecieParameterList(itemData.species);
            // Set item id
            id = itemData.polyId;
            // Set item title
            title = itemData.litra;
            // Set action number
            actionNumber = par.actionNumber ? par.actionNumber.toString() : '-';
            if (combi) {
                actionNumber = itemData.actionNumber.toString();
                main = false;
            }
            // Set action type
            action = itemData.species;
            if (itemData.species !== 'Afgrænsning') {
                action = itemData.species.split(':')[1].trim();
            }
            // Set year and age
            year = itemData.year ? parseInt(itemData.year) : "-";
            year = year > moment().year() ? '-' : year;
            const age = year !== '-' ? (moment().year() - year).toString() : '-';
            year = year.toString() + ' (' + age + ')';
            // Set lineLength
            if (itemData.lineLength && typeof itemData.lineLength === 'number') {
                lineLength = itemData.lineLength !== 0 ? itemData.lineLength.toFixed(0).split('.').join(',') : "-";
            }
            if (combi) {
                lineLength = '-';
            }
            // Set area
            // if (itemData.geoType === 'Polygon' && itemData.area && typeof itemData.area === 'number') {
            //     area = itemData.area.toFixed(2).replace('.', ',');
            // }
            if (itemData.area && typeof itemData.area === 'number') {
                area = itemData.area.toFixed(2).replace('.', ',');
            }
            // Set units and points
            if (action !== '-' && action !== 'Afgrænsning') {
                const out = getActionUnitsAndPoints(itemData.species, itemData.geoType, itemData.geoType === 'Line' ? itemData.lineLength : itemData.area, cultivatedArea, this.state.planType);
                maxReached = out.maxReached;
                units = out.units.toFixed(2).replace('.', ',');
                points = out.points.toFixed(1).replace('.', ',');
                if (combi) {
                    units = '-';
                    points = '-';
                }
            }
            // Set note
            if (typeof itemData.note === "number") {
                note = itemData.note.toString();
            } else {
                note = itemData.note === "" ? "-" : itemData.note;
            }
            // Set geometry type
            geoType = itemData.geoType;
        }

        // Return item data
        return ({
            main,
            id,
            title,
            actionNumber,
            action,
            year,
            lineLength,
            area,
            polyArea,
            units,
            points,
            note,
            geoType,
            maxReached
        })
    }

    listSummaryItem(item, content, endAdornment) {
        return (
            <div style={{ marginRight: 24 }}>
                <Typography
                    variant="body1"
                    style={{ fontSize: 14, fontWeight: 500, marginRight: 8 }}
                >
                    {item}
                </Typography >
                <Typography
                    variant="body1"
                    style={{ fontSize: 14 }}
                >
                    {content}{endAdornment}
                </Typography >
            </div>
        )
    }

    listSummaryItemPaired(title, text, data) {
        return (
            <div style={{ marginRight: 24 }}>
                <div style={{ display: 'flex' }}>
                    <Typography
                        variant="body1"
                        style={{ fontSize: 14, fontWeight: 500, marginRight: 6 }}
                    >
                        {title}
                    </Typography >

                    <Typography
                        variant="body1"
                        style={{ fontSize: 14 }}
                    >
                        {text}
                    </Typography >
                </div>

                <Typography
                    variant="body1"
                    style={{ fontSize: 14 }}
                >
                    {data}
                </Typography >
            </div>
        )
    }

    listSummary(data) {
        // Set data
        let featNum = 0;
        let unitsTotal = 0;
        let pointsTotal = this.state.planType === 5 ? 100 : 0;
        let areaTotal = this.state.totalArea;
        let birdsTotal = 0;
        let partridgesTotal = 0;
        let actionAmountObj = {};

        // Check if any filter is active
        const filActive = this.state.bpFilterActionsActive || this.state.bpFilterRangeActive || this.state.bpFilterSearchActive;
        
        const actionsTypeList = getSpeciesListBasedOnCategory('biotopePlan', 'skovkortet', 'full');
        actionsTypeList.forEach(el => {
            if (el[0] !== 'Afgrænsning') {
                actionAmountObj = {
                    ...actionAmountObj,
                    [el[0]]: {
                        actionNumber: el[1].actionNumber,
                        unitType: el[1].unitType,
                        value: 0,
                        type: el[1].geometryType,
                    }
                }
            }
        })

        // Set number of actions
        featNum = data.length;

        // Set other parameters
        for (let i = 0; i < data.length; i++) {
            const typeStr = data[i].actionNumber + ': ' + data[i].action;
            let addValue = 0;
            if (actionAmountObj[typeStr].unitType === 'length') {
                addValue = data[i].lineLength !== '-' ? parseFloat(data[i].lineLength) : 0;
            } else if (actionAmountObj[typeStr].unitType === 'area') {
                addValue = data[i].area !== '-' ? parseFloat(data[i].area.replace(',', '.')) : 0;
            } else if (actionAmountObj[typeStr].unitType === 'quantity') {
                addValue = 1;
            }
            actionAmountObj[typeStr].value = actionAmountObj[typeStr].value + addValue;
        }

        // Calculate total points
        Object.keys(actionAmountObj).forEach(key => {
            if (actionAmountObj[key].value > 0) {
                // Get units and points
                let amount = actionAmountObj[key].value
                if (key === "11: Lærkepletter" && actionAmountObj[key].value % 2) {
                    amount = amount - 1;
                }
                const up = getActionUnitsAndPoints(key, actionAmountObj[key].type, amount, this.state.cultivatedArea, this.state.planType);
                unitsTotal = unitsTotal + up.units;
                pointsTotal = pointsTotal + up.points;
            }
        })

        // Add additional points
        if (!filActive && (this.state.planType === 1 || this.state.planType === 3) && this.state.naturePlan) {
            unitsTotal = unitsTotal + 1;
            pointsTotal = pointsTotal + 10;
        }

        // Calculate number of birds for release
        if (this.state.planType !== 5) {
            if (this.state.planType === 1 && pointsTotal >= 100) {
                birdsTotal = areaTotal * 7;
                partridgesTotal = areaTotal * 4;
            } else if (this.state.planType === 3 && pointsTotal >= 85) {
                birdsTotal = areaTotal * 7;
                partridgesTotal = areaTotal * 4;
            } else {
                birdsTotal = areaTotal;
                partridgesTotal = areaTotal;
            }
        } else {
            partridgesTotal = 4 * areaTotal;
            birdsTotal = 7 * areaTotal;
            if (pointsTotal > 149) {
                birdsTotal = 12 * areaTotal;
            } else if (pointsTotal > 139) {
                birdsTotal = 11 * areaTotal;
            } else if (pointsTotal > 129) {
                birdsTotal = 10 * areaTotal;
            } else if (pointsTotal > 119) {
                birdsTotal = 9 * areaTotal;
            } else if (pointsTotal > 109) {
                birdsTotal = 8 * areaTotal;
            } 
        }

        // Check for number of features
        if (this.state.featureNum !== featNum.toFixed(0)) {
            this.setState({
                featureNum: featNum.toFixed(0),
            })
        }

        // Check if numbers should be floored (only when filter is not active)
        let unitsTotalDisp = unitsTotal.toFixed(2).replace('.', ',');
        let pointsTotalDisp = pointsTotal.toFixed(1).replace('.', ',');
        if (!filActive) {
            unitsTotalDisp = unitsTotal.toFixed(1).replace('.', ',')
            pointsTotalDisp = Math.floor(pointsTotal).toFixed(0);
        } 

        return (
            <div>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    {(this.state.planType === 1 || this.state.planType === 3) && this.listSummaryItem('Naturplan', this.state.naturePlan ? "Tilknyttet" : "Ikke tilknyttet")}
                    {this.listSummaryItem('Samlet areal', areaTotal.toFixed(2).replace('.', ','), ' ha')}
                    {this.listSummaryItem('Dyrket areal', this.state.cultivatedArea, ' ha')}
                    {this.listSummaryItem('Antal tiltag', featNum)}
                    {this.listSummaryItem('Antal enheder', unitsTotalDisp)}
                    {this.listSummaryItem('Antal point', pointsTotalDisp)}
                    {this.listSummaryItemPaired('Antal fugle, der kan sættes ud', '(samlet | agerhøns)', [Math.floor(birdsTotal).toFixed(0), ' | ', Math.floor(partridgesTotal).toFixed(0)])}
                </div>
            </div>
        )
    }

    handleSearchTextChange = event => {
        if (event.target.value !== '') {
            this.setState({ bpFilterSearchActive: true });
        } else {
            this.setState({ bpFilterSearchActive: false });
        }
        this.setState({ searchText: event.target.value, updateContent: true })
    };

    handleRequestSort = (event, property) => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Rows sorting clicked, property: ${property}`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        let numeric = this.state.rows.filter(r => { return r.id === property })
        numeric = numeric[0].numeric;
        const orderBy = property;
        let order = 'desc';

        if (this.state.orderBy === property && this.state.order === 'desc') {
            order = 'asc';
        }
        this.setState({ order, orderBy, numeric, updateContent: true });
    };

    handleColumnClick = event => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Coloumn choser opened`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        this.setState({ anchorEl: event.currentTarget });
    };

    handleColumnClose = () => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Coloumn choser closed`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        this.setState({ anchorEl: null });
    };

    handleFilterClick = event => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Filter opened`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        this.setState({ anchorEb: event.currentTarget });
    };

    handleFilterClose = () => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Filter closed`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        this.setState({ anchorEb: null });
    };

    handleRowClick = (event, data) => {
        event.preventDefault();
        const newAnchor = {
            xPos: event.clientX - 2,
            yPos: event.clientY - 4,
        }
        this.setState({ anchorNa: newAnchor, naviData: data });
    };

    handleRowClickAnchorButtonClose = () => {
        const newAnchor = {
            xPos: null,
            yPos: null
        }
        this.setState({ anchorNa: newAnchor, naviData: null });
    };

    handleNavigateToMapClick = () => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Navigate to list item: ${this.state.naviData.id}`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        this.props.rowClick(this.state.naviData)
        this.handleCloseList();
    }

    handleShowFilteredInMapClick = () => {
        if (this.props.filterShowFilterInMap && this.props.filterCellIds.length === this.state.listSummaryData.length) {
            this.props.onSetShowFilterInMap(false);
            this.props.onRedrawCells();
        } else {
            this.props.onSetShowFilterInMap(true);
            this.handleCloseList();
            this.props.onRedrawCells();
        }
    }

    handleChangePage = (event, page) => {
        this.setState({ page });
    };

    handleChangeRowsPerPage = event => {
        this.setState({ rowsPerPage: event.target.value });
    };

    handleCloseList = () => {
        this.props.onSetBiotopePlanListActive(false);
        // Send event to Analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Biotope plan open: ${false}`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
    }

    handleResetList = () => {
        // Send event to analytics
        ReactGA.event({
            category: 'Biotope plan list',
            action: `Reset list`,
            label: `User: ${this.props.currentAuthUser.name}`
        });
        // Reset filters
        this.state.bpFilterRanges.forEach(r => {
            r.newRange = r.initRange;
        })
        this.state.bpFilterActions.forEach(s => {
            s.checked = true;
        })

        this.setState({ bpFilterActionsActive: false, bpFilterRangeActive: false, bpFilterSearchActive: false })

        // Reset search text
        this.setState({ searchText: '' })

        // Reset columns
        this.state.rows.forEach((r, i) => {
            r.checked = true;
        })

        // Reset sorting
        this.setState({ order: 'asc', orderBy: 'action' })

        // Reset show filter in map
        this.props.onSetShowFilterInMap(false);
        this.props.onSetFilterCellIds(null);
        this.props.onRedrawCells();

        // Update content
        this.setState({ updateContent: true })
    }


    speciesFilter = (spc) => {
        for (let i = 0; i < this.state.bpFilterActions.length; i++) {
            if (spc === this.state.bpFilterActions[i].label) {
                return !this.state.bpFilterActions[i].checked;
            }
        }
    }

    rangesFilter = (id, columnEntry) => {
        const N = this.state.bpFilterRanges.length;
        let tVar = '-';
        if (id === 'year') {
            tVar = parseFloat(columnEntry.split('(').pop().split(')')[0])
        } else if (id === 'thinning' && columnEntry !== '-') {
            tVar = columnEntry.map(el => {
                return moment(el.date).year();
            })
        } else {
            tVar = parseFloat(columnEntry.split(',').join('.'));
        }
        let filteredOut = false;
        for (let i = 0; i < N; i++) {
            if (this.state.bpFilterRanges[i].id === id && this.state.bpFilterRanges[i].checked && (this.state.bpFilterRanges[i].newRange[0] !== this.state.bpFilterRanges[i].initRange[0] || this.state.bpFilterRanges[i].newRange[1] !== this.state.bpFilterRanges[i].initRange[1])) {
                if (columnEntry === '-' || columnEntry === '- (-)' || tVar === '-') {
                    filteredOut = true;
                    break;
                } else if (Array.isArray(tVar)) {
                    filteredOut = true;
                    for (let j = 0; j < tVar.length; j++) {
                        if (tVar[j] >= this.state.bpFilterRanges[i].newRange[0] && tVar[j] <= this.state.bpFilterRanges[i].newRange[1]) {
                            filteredOut = false;
                            break;
                        }
                    }
                } else if (tVar < this.state.bpFilterRanges[i].newRange[0] || tVar > this.state.bpFilterRanges[i].newRange[1]) {
                    filteredOut = true;
                    break;
                }
            }
        }
        return filteredOut;
    }

    renderTableRows = () => {
        const { classes } = this.props;
        let lastId = '';
        let summaryData = [];
        let ids = [];
        let content = stableSort(this.state.listDataNew, getSorting(this.state.order, this.state.orderBy, this.state.numeric))
            //.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((n, i, elements) => {
                // Filter and Search
                let searchFound = false;
                let isFilteredOutActions = false;
                let isFilteredOutRange = false;

                // Apply actions filter
                isFilteredOutActions = this.speciesFilter(n.action);

                // Run through column values in row
                if (!isFilteredOutActions) {
                    for (let i = 0; i < this.state.rows.length; i++) {
                        // Apply filters on ranges
                        let tVar = n[this.state.rows[i].id];
                        if (this.state.rows[i].id === 'area') {
                            tVar = n.polyArea;
                        }
                        isFilteredOutRange = this.rangesFilter(this.state.rows[i].id, tVar)
                        if (isFilteredOutRange) { break }

                        // Apply search
                        if (this.state.rows[i].checked) {
                            if (this.state.searchText.length > 0 && !isFilteredOutActions && !isFilteredOutRange) {
                                const searchTxt = this.state.searchText.toLowerCase();
                                // Tokenice search text to check for litra search
                                let tokStr = searchTxt.split(';');
                                if (tokStr.length === 1) {
                                    if (typeof n[this.state.rows[i].id] === 'string') {
                                        let text = n[this.state.rows[i].id].toLowerCase();
                                        // Search for string i all columns
                                        if (text.indexOf(searchTxt) !== -1) {
                                            searchFound = true;
                                            break;
                                        }
                                    }
                                } else if (tokStr.length > 1) {
                                    if (this.state.rows[i].id === 'actionNumber') {
                                        let text = n[this.state.rows[i].id].toLowerCase();
                                        text = n[this.state.rows[i - 1].id].toLowerCase() + text;
                                        if (tokStr.includes(text) || tokStr.includes(n[this.state.rows[i - 1].id].toLowerCase())) {
                                            searchFound = true;
                                            break
                                        }
                                    }
                                }

                            } else {
                                searchFound = true;
                            }
                        }
                    }
                }

                // Display row data based on filters, search and selected rows
                let rowContent = null;
                if ((isFilteredOutActions || isFilteredOutRange) || (this.state.searchText !== '' && !searchFound)) {
                    rowContent = null;
                } else {
                    // Include data in summary
                    summaryData.push(n);
                    // Fill table row
                    rowContent = this.state.rows.map((r, j) => {
                        // Check for more entries in one item
                        let tableCellInput = n[r.id]
                        let body = classes.cellStyle;
                        if (r.id === 'points' && n.maxReached) {
                            body = classes.cellStyleMaxReached;
                        }
                        
                        // Check if entry is part of previous cell and set cell input to empty string
                        if (n.id === lastId && !n.main) {
                            if (r.id !== 'action') {
                                tableCellInput = '';
                                body = null;
                            } else {
                                body = classes.cellStyleItalic;
                            }
                        }
                        // Return item
                        return r.checked ? <TableCell
                            key={j}
                            align="left"
                            size="small"
                            classes={{
                                root: classes.cellNoBorder,
                                body: body,
                                // body: ids.length === 0 ? // Check if first line
                                //     (!n.main ? (r.id === 'action' ? classes.bodyFontItalic : null) : null)
                                //     : (n.id !== lastId ? // Check if new litra
                                //         (!n.main ? (r.id === 'action' ? classes.cellStyleItalic : classes.cellStyle) : classes.cellStyle) // New litra. If not main then italic on specie
                                //         : (r.id === 'action' || r.id === 'note' ? null : !n.main ? (r.id === 'action' ? classes.cellStyleItalic : classes.cellStyle) : classes.cellStyle)) // Same litra. Remove top border and add italic if not main
                            }}
                        >
                            {tableCellInput}
                        </TableCell> : null
                    })
                    // Save last id
                    lastId = n.id;
                }

                return (
                    <TableRow
                        hover
                        onClick={event => this.handleRowClick(event, n)}
                        role="checkbox"
                        tabIndex={-1}
                        key={i}
                    >
                        {rowContent}
                    </TableRow>
                );
            })

        // Set summary data and content in state
        this.setState({ listSummaryData: summaryData, tableBodyContent: content, updateContent: true })
    }

    renderToolbar(header) {
        const { classes } = this.props;
        const { anchorEl, anchorEb, litraNum,
            lengthTotal, areaTotal, forestAreaTotal, areaTotalFoliage,
            areaTotalNeedle, treeCountTotal, volumeTotal,
            intensiveAreaPrc, biodiversityAreaPrc, primavelAreaPrc,
            CO2Total, showTreeCount, showCO2
        } = this.state;
        let rows = [...this.state.rows];
        let summary = [
            { label: 'Litraer', checked: true, value: litraNum },
            { label: 'Længde (m)', checked: true, value: lengthTotal },
            { label: 'Samlet (ha)', checked: true, value: areaTotal },
            { label: 'Skov (ha)', checked: true, value: forestAreaTotal },
            { label: 'Løv (ha)', checked: true, value: areaTotalFoliage },
            { label: 'Nål (ha)', checked: true, value: areaTotalNeedle },
            { label: 'Stamtal', checked: showTreeCount, value: treeCountTotal },
            { label: 'Vedmasse (m3)', checked: true, value: volumeTotal },
            { label: 'CO2-e (ton)', checked: showCO2, value: CO2Total },
            { label: 'Intensivt (%)', checked: true, value: intensiveAreaPrc },
            { label: 'Biodiversitet (%)', checked: true, value: biodiversityAreaPrc },
            { label: 'Urørt (%)', checked: true, value: primavelAreaPrc }
        ]

        return (
            <Toolbar
                className={classNames(classes.toolbarRoot)}
            >
                <div className={classes.title}>
                    <Typography variant="h6" id="tableTitle">
                        {header}
                    </Typography>
                </div>

                <div className={classes.spacer} />

                <div className={classes.actions}>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <SearchIcon />
                        </div>

                        <Tooltip title="Søg i biotopplan">
                            <InputBase
                                placeholder="Søg"
                                onChange={this.handleSearchTextChange}
                                value={this.state.searchText}
                                classes={{
                                    root: classes.inputRoot,
                                    input: classes.inputInput,
                                }}
                                inputProps={{ 'aria-label': 'search' }}
                            />
                        </Tooltip>
                    </div>

                    <Tooltip title="Filtrere biotopplan">
                        <IconButton
                            aria-label="Filtrere liste"
                            onClick={this.handleFilterClick}
                            color={this.state.bpFilterActionsActive || this.state.bpFilterRangeActive ? "primary" : "default"}
                        >
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>

                    {/* <Tooltip title={(this.props.filterShowFilterInMap && this.props.filterCellIds.length === this.state.listSummaryData.length) ? "Fjern filtrering i kortet" : "Se resultat i kort"}>
                        <IconButton
                            aria-label="Se resultat i kort"
                            onClick={this.handleShowFilteredInMapClick}
                            disabled={(!this.state.bpFilterRangeActive && !this.state.bpFilterActionsActive && !this.state.bpFilterSearchActive && !this.props.filterShowFilterInMap) || this.state.listSummaryData.length === 0}
                            color={(this.props.filterShowFilterInMap && this.props.filterCellIds.length === this.state.listSummaryData.length) ? "primary" : "default"}
                        >
                            <ArrowForwardIcon />
                        </IconButton>
                    </Tooltip>

                    <div style={{ height: 36, width: 2, margin: "0px 6px", backgroundColor: '#5EBD3E' }} /> */}

                    <Menu
                        id="simple-menu"
                        keepMounted
                        anchorEl={anchorEb}
                        open={Boolean(anchorEb)}
                        onClose={this.handleFilterClose}
                        classes={{
                            paper: classes.menuContainer,
                        }}
                    >
                        <Filter
                            ranges={this.state.bpFilterRanges}
                            onRangeChange={this.onRangeChange.bind(this)}
                            species={this.state.bpFilterActions}
                            onSpecieChange={this.onSpecieChange.bind(this)}
                            speciesAll={!this.state.bpFilterActionsActive}
                            onSpecieChangeAll={this.onSpecieChangeAll.bind(this)}
                            applyFilters={this.applyFilters.bind(this)}
                            resetFilters={this.resetFilters.bind(this)}
                            type="biotopePlan"
                        />
                    </Menu>

                    <Tooltip title="Vælg kolonner">
                        <IconButton
                            aria-label="Column selection"
                            onClick={this.handleColumnClick}
                            color={this.state.rows.every(item => item.checked) ? "default" : "primary"}
                        >
                            <ColumnIcon />
                        </IconButton>
                    </Tooltip>

                    <Menu
                        id="simple-menu"
                        keepMounted
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={this.handleColumnClose}
                        classes={{
                            paper: classes.menuContainer,
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body1">
                                Kolonner
                            </Typography>

                            {this.state.rows.map((row, i) => (
                                <FormControlLabel
                                    key={i}
                                    control={
                                        <Checkbox
                                            checked={row.checked}
                                            onChange={() => (
                                                rows[i].checked = !rows[i].checked,
                                                this.setState({ rows, updateContent: true }),
                                                cookies.set('columnStateBiotopePlan', rows))}
                                            name="checkedB"
                                            color="primary"
                                        />
                                    }
                                    label={row.label}
                                />
                            ))}
                        </div>
                    </Menu>

                    <Menu
                        id="navi-menu"
                        keepMounted
                        open={this.state.anchorNa.yPos !== null}
                        onClose={this.handleRowClickAnchorButtonClose}
                        anchorReference="anchorPosition"
                        anchorPosition={
                            this.state.anchorNa.xPos !== null && this.state.anchorNa.yPos !== null
                                ? { top: this.state.anchorNa.yPos, left: this.state.anchorNa.xPos }
                                : undefined
                        }
                        classes={{
                            paper: classes.naviContainer,
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Button
                                color="primary"
                                onClick={this.handleNavigateToMapClick}
                            >
                                Naviger til kort
                                <IconChevronRight />
                            </Button>
                        </div>
                    </Menu>

                    {/* <Print
                        title={`Biotopplan for ${this.props.biotopePlansUser[this.props.selectedBiotopePlan].planName}, ${this.props.biotopePlansUser[this.props.selectedBiotopePlan].name}`}
                        columns={this.state.rows}
                        data={this.state.listSummaryData}
                        summary={summary}
                        company={this.props.org ? this.props.org.name : ''}
                    />

                    <Download
                        title={`Biotopplan for ${this.props.biotopePlansUser[this.props.selectedBiotopePlan].planName} ${this.props.biotopePlansUser[this.props.selectedBiotopePlan].name}`}
                        columns={this.state.rows}
                        data={this.state.listSummaryData}
                    /> */}

                    <Tooltip title="Genload biotopplan">
                        <IconButton aria-label="Reset list" onClick={this.handleResetList}>
                            <ReplayIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Luk biotopplan">
                        <IconButton aria-label="Close list" onClick={this.handleCloseList}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            </Toolbar>
        )
    }

    render() {
        const { classes } = this.props;
        const { rows, order, orderBy } = this.state;
        const header = this.props.biotopePlansUser[this.props.selectedBiotopePlan].planName + ", " + this.props.biotopePlansUser[this.props.selectedBiotopePlan].planType + ", " + this.props.biotopePlansUser[this.props.selectedBiotopePlan].name;
        let content = <TableBody>
            {this.state.tableBodyContent}
        </TableBody>
        let contentNoData = null;
        if (this.state.listSummaryData.length === 0) {
            content = null
            contentNoData = <Typography style={{ padding: 16 }}>
                Listen har ingen indhold
            </Typography>
        }

        return (
            <div className={classes.root}>
                <Paper className={classes.paper}>
                    {this.renderToolbar(header)}

                    <div className={classes.tableWrapper} style={{ height: this.state.height - 150 }}>
                        <Table stickyHeader className={classes.table} aria-labelledby="bevoksningsliste">
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={this.handleRequestSort}
                                rowCount={this.state.listDataShow.length}
                                rows={rows}
                            />
                            {content}
                            {/* <TableBody>
                                {this.state.tableBodyContent}
                            </TableBody> */}
                        </Table>
                        {contentNoData}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            borderTop: '1px solid #E0E0E0',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 16,
                            borderBottomLeftRadius: 5,
                            borderBottomRightRadius: 5
                        }}
                    >
                        {this.listSummary(this.state.listSummaryData)}
                    </div>
                </Paper>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        speciesScheme: state.layout.speciesScheme,
        biotopePlanFeatures: state.biotopePlans.features,
        biotopePlanPoints: state.biotopePlans.points,
        currentAuthUser: state.user.currentAuthUser,
        selectedBiotopePlan: state.biotopePlans.currentlySelectedBiotopePlanId,
        biotopePlansUser: state.biotopePlans.biotopePlansUser,
        org: state.org.org,
        filterState: state.inventory.filterState,
        filterShowFilterInMap: state.inventory.showFilterInMap,
        filterCellIds: state.inventory.filterCellIds,
    };
}

const mapDispatchToProps = dispatch => {
    return {
        onSetFilterState: (s) => dispatch(actions.setFilterState(s)),
        onSetFilterCellIds: (s) => dispatch(actions.setFilterCellIds(s)),
        onSetShowFilterInMap: (s) => dispatch(actions.setShowFilterInMap(s)),
        onRedrawCells: () => dispatch(actions.redrawCells()),
        onSetBiotopePlanListActive: (state) => dispatch(actions.setBiotopePlanListActive(state)),
    };
}

EnhancedTable.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withStyles(styles)(EnhancedTable)));
